// BM-H588, 23 September 2026. Draw or type a signature on the onboarding forms.
//
// Controlled by the parent, which holds the mode, the typed name and the pad's
// ref, so its own submit and validation stay where they were. Rules live in
// ../lib/clientSignature.js.
import React from "react";
import SignatureCanvas from "react-signature-canvas";
import { Pen, Type } from "lucide-react";
import { Label } from "./ui/label";
import { SIGNATURE_MODES, typedSignatureMatches } from "../lib/clientSignature";

export default function ClientSignatureField({
  mode,
  onModeChange,
  typedName,
  onTypedNameChange,
  canvasRef,
  fullName,
  padTestId = "signature-pad",
  clearTestId = "clear-signature",
  testIdPrefix = "",
}) {
  const matches = typedSignatureMatches(typedName, fullName);
  const typedStarted = (typedName || "").trim() !== "";

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}signature-field`}>
      <Label className="text-txt font-medium">
        Signature <span className="text-status-red">*</span>
      </Label>
      <p className="text-xs text-slate-500">
        Draw your signature, or type your full name. Either is a valid electronic signature.
      </p>

      <div className="flex gap-2" role="tablist" aria-label="How would you like to sign?">
        {SIGNATURE_MODES.map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => onModeChange(m)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
              mode === m
                ? "bg-teal text-white border-teal"
                : "bg-transparent text-txt-muted border-navy-border hover:bg-white/5"
            }`}
            data-testid={`${testIdPrefix}sig-mode-${m}`}
          >
            {m === "draw" ? <Pen className="w-4 h-4" /> : <Type className="w-4 h-4" />}
            {m === "draw" ? "Draw" : "Type"}
          </button>
        ))}
      </div>

      {mode === "draw" ? (
        <>
          <div className="signature-paper border-2 border-navy-border rounded-md overflow-hidden" data-testid={padTestId}>
            <SignatureCanvas
              ref={canvasRef}
              penColor="#000000"
              backgroundColor="rgba(255,255,255,1)"
              canvasProps={{ className: "w-full", height: 150, style: { width: "100%", height: 150, background: "white" } }}
            />
          </div>
          <button
            type="button"
            onClick={() => canvasRef.current?.clear()}
            className="text-xs text-slate-500 hover:text-txt underline"
            data-testid={clearTestId}
          >
            Clear signature
          </button>
        </>
      ) : (
        <div>
          <input
            type="text"
            value={typedName}
            onChange={(e) => onTypedNameChange(e.target.value)}
            placeholder="Type your full name"
            autoComplete="name"
            aria-label="Type your full name as your signature"
            className="w-full px-4 py-4 text-2xl font-sans text-center signature-paper border-2 border-dashed border-navy-border rounded-md focus:outline-none focus:ring-2 focus:ring-teal/30"
            data-testid={`${testIdPrefix}typed-signature-input`}
          />
          {typedStarted && matches && (
            <p className="text-center mt-2 text-xs text-slate-500" data-testid={`${testIdPrefix}typed-signature-ok`}>
              Your typed name will be used as your electronic signature.
            </p>
          )}
          {typedStarted && !matches && (
            <p className="text-center mt-2 text-xs text-status-amber" data-testid={`${testIdPrefix}typed-signature-mismatch`}>
              Your typed signature should match your full name as entered above.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
